import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { useNavigate } from 'react-router-dom'
import { getTheme } from '../lib/theme'

export default function SetPassword() {
  const { user, setPassword, authActionError } = useAuth()
  const navigate = useNavigate()
  const [password, setPwd] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [hasSession, setHasSession] = useState(false)
  const [timedOut, setTimedOut] = useState(false)

  // The invite/recovery token (hash) or PKCE code (query) establishes a session
  // asynchronously after the page loads. Wait for it — checking only once on
  // mount races the exchange and falsely reports a broken link. We watch the
  // auth context's `user` and also poll getSession, with a grace timeout.
  useEffect(() => {
    if (user) { setHasSession(true); return }
    let active = true
    const check = () => supabase.auth.getSession().then(({ data: { session } }) => {
      if (active && session) setHasSession(true)
    })
    check()
    const interval = setInterval(check, 600)
    const timer = setTimeout(() => { if (active) setTimedOut(true) }, 5000)
    return () => { active = false; clearInterval(interval); clearTimeout(timer) }
  }, [user])

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return }
    if (password !== confirm) { setError('Passwords do not match.'); return }
    setLoading(true)
    const { error } = await setPassword(password)
    if (error) {
      setError(error.message)
      setLoading(false)
    } else {
      setSuccess(true)
      try { localStorage.setItem('inv_last_activity', String(Date.now())) } catch (e) { /* ignore */ } // fresh idle sign-out clock
      setTimeout(() => navigate('/', { replace: true }), 1200)
    }
  }

  const ready = hasSession || Boolean(user)
  const checkingSession = !ready && !timedOut
  const linkBroken = Boolean(authActionError) || (timedOut && !ready)

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg)',
      padding: '20px'
    }}>
      <div style={{ width: '100%', maxWidth: '380px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img
            src={getTheme() === 'light' ? '/Decent-Startup_Logo_Black.png' : '/Decent-Startup_Logo_White.png'}
            alt="Decent Startup"
            style={{ width: '160px', height: 'auto', margin: '0 auto 20px', display: 'block' }}
          />
          <h1 style={{ fontSize: '22px', fontWeight: '600', marginBottom: '6px' }}>Set Your Password</h1>
          <p style={{ color: 'var(--text-3)', fontSize: '13px' }}>Choose a password to finish setting up your account.</p>
        </div>

        <div className="card">
          {linkBroken ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div className="alert alert-danger">
                {authActionError || 'This link is invalid or has expired.'}
              </div>
              <p style={{ color: 'var(--text-2)', fontSize: '13px', lineHeight: 1.6 }}>
                Ask an admin to send you a fresh invite, or use “Forgot password” to request a new link.
              </p>
              <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => navigate('/login', { replace: true })}>
                Back to Sign In
              </button>
            </div>
          ) : success ? (
            <div className="alert alert-success">Password set. Signing you in…</div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input
                  className="input"
                  type="password"
                  value={password}
                  onChange={e => setPwd(e.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm Password</label>
                <input
                  className="input"
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  required
                />
              </div>
              {error && <div className="alert alert-danger">{error}</div>}
              <button
                className="btn btn-primary btn-lg"
                type="submit"
                disabled={loading || checkingSession}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                {loading ? 'Saving…' : checkingSession ? 'Verifying link…' : 'Set Password & Continue'}
              </button>
            </form>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: '24px', color: 'var(--text-3)', fontSize: '12px' }}>
          Questions? Contact <a href="mailto:inventory@decentstartup.com" style={{ color: 'var(--accent)', textDecoration: 'none' }}>inventory@decentstartup.com</a>
        </p>
      </div>
    </div>
  )
}
